import { Button, Grid, IconButton, InputAdornment, Link, MenuItem, TableCell, TableRow, TextField } from '@mui/material';
import { getCompanies, getInvoices } from 'api';
import { Page, Table } from 'components';
import { usePersistentListParameters } from 'context';
import { CompanyDto, InvoiceDto, InvoicesParameters } from 'dtos';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'utils';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Clear } from '@mui/icons-material';

export default function Invoicing() {
  const [isGettingCompanies, setIsGettingCompanies] = useState<boolean>(false);
  const [isGettingInvoices, setIsGettingInvoices] = useState<boolean>(false);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);

  const navigate = useNavigate();
  const persistentListParameters = usePersistentListParameters();

  const onFilter = useDebounce((parameters: InvoicesParameters) => {
    setIsGettingInvoices(true);
    getInvoices(parameters)
      .then(({ totalCount, value }) => {
        setTotalInvoices(totalCount!);
        setInvoices(value);
      })
      .finally(() => {
        persistentListParameters.setState('invoices', parameters);
        setIsGettingInvoices(false);
      });
  }, 100);

  useEffect(() => {
    setIsGettingCompanies(true);
    getCompanies()
      .then(({ value }) => {
        setCompanies(value);
      })
      .finally(() => {
        setIsGettingCompanies(false);
      });
  }, []);

  useEffect(() => {
    onFilter(persistentListParameters.state['invoices']);
  }, [
    onFilter,
    persistentListParameters.state['invoices'].page,
    persistentListParameters.state['invoices'].pageSize,
    persistentListParameters.state['invoices'].sortBy,
    persistentListParameters.state['invoices'].sortDirection
  ]);

  const onReset = () => {
    onFilter(persistentListParameters.initialState['invoices']);
  };

  const onSortChange = (propertyName: string, sortDirection: 'asc' | 'desc') => {
    persistentListParameters.setState('invoices', {
      ...persistentListParameters.state['invoices'],
      sortBy: propertyName,
      sortDirection
    });
  };

  const onChangePage = (page: number) => {
    persistentListParameters.setState('invoices', {
      ...persistentListParameters.state['invoices'],
      page
    });
  };

  const onChangePageSize = (pageSize: number) => {
    persistentListParameters.setState('invoices', {
      ...persistentListParameters.state['invoices'],
      pageSize
    });
  };

  return (
    <Page title='Invoices' isLoading={isGettingInvoices}>
      <Table
        actions={
          <Grid container justifyContent='space-between'>
            <Grid item xs={12} sm={6} container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label='Filter text'
                  onChange={e => {
                    persistentListParameters.setState('invoices', {
                      ...persistentListParameters.state['invoices'],
                      filterText: e.target.value
                    });
                  }}
                  size='small'
                  value={persistentListParameters.state['invoices'].filterText}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label='Company'
                  onChange={e => {
                    persistentListParameters.setState('invoices', {
                      ...persistentListParameters.state['invoices'],
                      companyId: e.target.value
                    });
                  }}
                  select
                  size='small'
                  SelectProps={{
                    displayEmpty: true,
                    endAdornment: persistentListParameters.state['invoices'].companyId ? (
                      <InputAdornment position='end' sx={{ mr: 1.5 }}>
                        <IconButton
                          onClick={() => {
                            persistentListParameters.setState('invoices', {
                              ...persistentListParameters.state['invoices'],
                              companyId: undefined
                            });
                          }}
                          size='small'
                        >
                          <Clear fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      <></>
                    )
                  }}
                  value={persistentListParameters.state['invoices'].companyId || null}
                >
                  {companies.map(company => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item>
                <Button
                  color='inherit'
                  onClick={() => {
                    onFilter(persistentListParameters.state['invoices']);
                  }}
                  variant='contained'
                >
                  FILTER
                </Button>
              </Grid>

              <Grid item>
                <Button color='inherit' onClick={onReset} variant='contained'>
                  RESET
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6} container justifyContent='flex-end'>
              <Grid item>
                <Button
                  color='inherit'
                  onClick={() => {
                    navigate('/invoicing/new');
                  }}
                  variant='contained'
                >
                  ADD NEW
                </Button>
              </Grid>
            </Grid>
          </Grid>
        }
        columns={[
          { label: 'Invoice #', propertyName: 'invoiceNumber' },
          { label: 'Job #', propertyName: 'jobNumber' },
          { label: 'Contract #', propertyName: 'jobContractNumber' },
          { label: 'PO #', propertyName: 'poNumber' },
          { label: 'Invoice Date', propertyName: 'invoiceDate' },
          { label: 'Status', propertyName: 'status' }
        ]}
        onChangePage={onChangePage}
        onChangePageSize={onChangePageSize}
        onSortChange={onSortChange}
        page={persistentListParameters.state['invoices'].page!}
        pageSize={persistentListParameters.state['invoices'].pageSize!}
        sortBy={persistentListParameters.state['invoices'].sortBy}
        sortDirection={persistentListParameters.state['invoices'].sortDirection}
        totalCount={totalInvoices}
      >
        {invoices.map((invoice, index) => (
          <TableRow key={invoice.id}>
            <TableCell>
              <Link
                component={ReactRouterLink}
                to={`/invoicing/${invoice.invoiceNumber}`}
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
              >
                {invoice.invoiceNumber}
              </Link>
            </TableCell>

            <TableCell>{invoice.job.jobNumber}</TableCell>

            <TableCell>
              {invoice.jobContractDetail.contractNumber} - {invoice.jobContractDetail.customer.companyName}
            </TableCell>

            <TableCell>{invoice.poNumber}</TableCell>

            <TableCell>{moment(invoice.invoiceDate).format('MM/DD/yyyy')}</TableCell>

            <TableCell>{invoice.isFinalized ? 'Invoiced' : 'Pre-processed'}</TableCell>
          </TableRow>
        ))}
      </Table>
    </Page>
  );
}
